<template>
  <div class="page-container mxmed-page">
    <portal to="page-name">MxMed Connect</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">MxMed Connect</h1>
        <button
          v-if="mappings"
          class="btn btn-sm"
          @click="showWebhookModal = true"
        >
          <fa-icon :icon="['fal', 'list-alt']" class="mr-1" />
          Logs de Webhook
        </button>
      </div>

      <div class="card-body" v-if="mxmedConfigured">
        <div class="loading loading-lg" v-if="loading"></div>
        <template v-else>
          <div class="mxmed-section">
            <h5 class="mxmed-section-title">Endereço</h5>
            <p class="text-gray mxmed-section-desc">
              Vincule a unidade Stenci a um endereço no MxMed.
            </p>
            <div class="input-group">
              <span class="input-group-addon">Endereço MxMed</span>
              <select
                class="form-select"
                v-model="selectedAddressId"
                :disabled="saving"
              >
                <option value="">[Selecione]</option>
                <option
                  v-for="addr in addresses"
                  :key="addr.id"
                  :value="addr.id"
                  :disabled="!addr.active"
                >
                  {{ formatAddress(addr) }}
                  <template v-if="!addr.active"> (inativo)</template>
                </option>
              </select>
              <button
                class="btn btn-primary input-group-btn"
                :class="{ loading: saving }"
                :disabled="saving || !addressChanged"
                @click="saveAddress"
              >Salvar</button>
            </div>
          </div>

          <template v-if="mappings">
            <professionals-section
              :mappings="mappings"
              :mxmed-professionals="mxmedProfessionals"
              :mxmed-insurances="mxmedInsurances"
              @update-mappings="mappings = $event"
            ></professionals-section>
            <webhook-logs-modal
              :show="showWebhookModal"
              @close="showWebhookModal = false"
            ></webhook-logs-modal>
          </template>
          <div class="mxmed-unconfigured" v-else>
            <div class="tile">
              <div class="tile-icon">
                <fa-icon :icon="['fal', 'info-circle']" class="text-gray" />
              </div>
              <div class="tile-content">
                <div class="tile-subtitle text-gray">
                  Selecione um endereço e salve para configurar os profissionais e convênios.
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="card-body" v-else-if="mxmedEnabled">
        <div class="empty">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'exclamation-triangle']" size="3x" />
          </div>
          <p class="empty-title h5">A integração com o MxMed não está configurada corretamente</p>
          <p class="empty-subtitle">
            A chave do cliente não foi informada ou é inválida. Acesse as
            <router-link to="/settings" class="text-primary">configurações</router-link>
            na aba Integração para informar a chave do cliente.
          </p>
        </div>
      </div>

      <div class="card-body" v-else>
        <div class="empty">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x" />
          </div>
          <p class="empty-title h5">A integração com o MxMed não está habilitada</p>
          <p class="empty-subtitle">
            Acesse as
            <router-link to="/settings" class="text-primary">configurações</router-link>
            na aba Integração para ativar esta funcionalidade.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import isValidCustomerKey from '@/app/mxmed/helpers';
import api from '../api';
import ProfessionalsSection from './ProfessionalsSection.vue';
import WebhookLogsModal from './WebhookLogsModal.vue';

export default {
  components: {
    ProfessionalsSection,
    WebhookLogsModal,
  },
  data() {
    return {
      loading: false,
      saving: false,
      mappings: null,
      addresses: [],
      mxmedProfessionals: [],
      mxmedInsurances: [],
      selectedAddressId: '',
      showWebhookModal: false,
    };
  },
  async mounted() {
    await this.load();
  },
  computed: {
    ...mapGetters(['branchSettings']),
    mxmedEnabled() {
      const { branchSettings } = this;
      return !!(branchSettings && branchSettings.mxmed && branchSettings.mxmed.enabled);
    },
    mxmedConfigured() {
      if (!this.mxmedEnabled) return false;
      const { customerKey } = this.branchSettings.mxmed;
      return !!customerKey && isValidCustomerKey(customerKey);
    },
    addressChanged() {
      if (!this.selectedAddressId) return false;
      if (!this.mappings) return true;
      return String(this.selectedAddressId) !== String(this.mappings.addressMappingId);
    },
  },
  methods: {
    async load() {
      if (!this.mxmedConfigured) {
        return;
      }
      this.loading = true;
      try {
        const [mappings, addresses, professionals, insurances] = await Promise.all([
          api.mapping.findOne(),
          api.address.find(),
          api.professional.find(),
          api.insurance.find(),
        ]);
        this.mappings = mappings;
        this.addresses = addresses.items;
        this.mxmedProfessionals = professionals.items;
        this.mxmedInsurances = insurances.items;
        if (this.mappings && this.mappings.addressMappingId) {
          this.selectedAddressId = this.mappings.addressMappingId;
        }
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    async saveAddress() {
      this.saving = true;
      try {
        this.mappings = await api.mapping.createOrUpdate({
          addressMappingId: String(this.selectedAddressId),
        });
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    formatAddress(addr) {
      return `${addr.street}, ${addr.number} — ${addr.city}/${addr.state}`;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.mxmed-page {
  .card-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .mxmed-section {
    margin-bottom: $layout-spacing-xl;
  }

  .mxmed-section-title {
    font-size: $font-size-lg;
    font-weight: 600;
    margin-bottom: $layout-spacing-sm;
  }

  .mxmed-section-desc {
    margin-bottom: $layout-spacing-sm;
    margin-top: 0;
  }

  .mxmed-unconfigured {
    margin-top: $layout-spacing-lg;
    padding: $layout-spacing-lg;
    background: $bg-color-dark;
    border-radius: $border-radius;

    .tile-icon {
      font-size: $layout-spacing-xl;
      margin-right: $layout-spacing;
    }
  }
}
</style>
